import type { NoteName } from '@tunasong/schemas'
import { midiNoteNumberFromFrequency } from '../note/note-frequencies.js'
import * as NoteLib from '../note/note-lib.js'
import { median } from '../vocal/statistics.js'
import type { PitchFrame } from '../vocal/types.js'
import { voicedFrames } from '../vocal/voicing.js'
import { MAJOR_TONICS, MINOR_TONICS } from './function-quiz.js'

/**
 * Pitch Trainer v1 (Vocal Pitch Training Phase 1) — the pure exercise core.
 *
 * Four generators, one per §6 exercise type, each taking an injectable RNG so a session is
 * reproducible in a test. Mirrors the Ear Training analyzer file-for-file: pool tables,
 * generators with an `rng = Math.random` parameter, a session-stat fold. Roots are drawn from
 * Function Quiz's spelling-preferred tonic tables so no exercise is ever named with a double
 * accidental.
 *
 * Nothing here knows about audio. Everything a phrase needs to be *sung* — transposition into
 * the singer's range, audition, capture, scoring — happens above this module.
 *
 * Spec: docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §6, §5.8
 */

/** The four exercise types, in the increasing difficulty §6 lays out. */
export type PitchExerciseType = 'tone' | 'interval' | 'vocalise' | 'phrase'

/** Interval difficulty. User-selected, never model-driven (§2). */
export type PitchIntervalLevel = 1 | 2 | 3

/**
 * The interval sets, by level (§6).
 *
 * P8 is in level 1 and is not in Ear Training's `INTERVAL_POOL`, which stops at M7 — recognising
 * an octave is trivial, producing one is not. So this module carries its own semitone table
 * rather than filtering the recognition pool.
 *
 * Ascending only in v1: descending is a genuinely separate vocal skill, for the same reason Ear
 * Training v1 deferred it and v1.5 added a direction picker.
 */
export type PitchIntervalKind = 'm2' | 'M2' | 'm3' | 'M3' | 'P4' | 'TT' | 'P5' | 'm6' | 'M6' | 'm7' | 'M7' | 'P8'

/**
 * Letters above the root for each interval, from its name: a third is two letters up whatever
 * its quality. The tritone is the augmented fourth (Bowie, GH #2328): above C it is F#, never Gb.
 */
const INTERVAL_LETTER_STEPS: Record<PitchIntervalKind, number> = {
  m2: 1,
  M2: 1,
  m3: 2,
  M3: 2,
  P4: 3,
  TT: 3,
  P5: 4,
  m6: 5,
  M6: 5,
  m7: 6,
  M7: 6,
  P8: 7,
}

const INTERVAL_SEMITONES: Record<PitchIntervalKind, number> = {
  m2: 1,
  M2: 2,
  m3: 3,
  M3: 4,
  P4: 5,
  TT: 6,
  P5: 7,
  m6: 8,
  M6: 9,
  m7: 10,
  M7: 11,
  P8: 12,
}

const LEVEL_1: readonly PitchIntervalKind[] = ['M2', 'M3', 'P4', 'P5', 'P8']
const LEVEL_2_ADDS: readonly PitchIntervalKind[] = ['m2', 'm3', 'm6', 'M6']
const LEVEL_3_ADDS: readonly PitchIntervalKind[] = ['TT', 'm7', 'M7']

/** Each level contains every interval of the level below it — the ladder never takes one away. */
export const INTERVAL_LEVEL_SETS: Record<PitchIntervalLevel, readonly PitchIntervalKind[]> = {
  1: LEVEL_1,
  2: [...LEVEL_1, ...LEVEL_2_ADDS],
  3: [...LEVEL_1, ...LEVEL_2_ADDS, ...LEVEL_3_ADDS],
}

export interface VocalisePattern {
  id: string
  /** Scale degrees, 1-indexed; 8 is the octave. */
  degrees: readonly number[]
}

/**
 * The canonical shapes every vocal method opens with (§6). The first exercises stepwise motion,
 * the other two arpeggiation — which is why they are separate patterns and not one list.
 */
export const VOCALISE_PATTERNS: readonly VocalisePattern[] = [
  { id: 'scale-run', degrees: [1, 2, 3, 4, 5, 4, 3, 2, 1] },
  { id: 'triad', degrees: [1, 3, 5, 3, 1] },
  { id: 'triad-octave', degrees: [1, 3, 5, 8, 5, 3, 1] },
]

/** Semitones above the tonic for scale degrees 1..8. Natural minor, per §6. */
const MAJOR_STEPS: readonly number[] = [0, 2, 4, 5, 7, 9, 11, 12]
const MINOR_STEPS: readonly number[] = [0, 2, 3, 5, 7, 8, 10, 12]

/**
 * The nominal octave every exercise is generated in — C4 to B4. It is never sung there: the host
 * runs `VocalLib.transposeIntoRange` before the phrase is auditioned or scored, so this is only
 * a starting point for the maths.
 */
const NOMINAL_TONIC_MIDI = 60

/** Seconds a single sustained tone is held (§6, exercise type 1). */
const TONE_HOLD_SECONDS = 2

/** Seconds per note in everything else — the note length the Ear Training audition already uses. */
const NOTE_SECONDS = 0.6

export type PitchExerciseQuality = 'major' | 'minor'

export interface PitchQuestion {
  type: PitchExerciseType
  /** MIDI numbers in generation order, before any range transposition. */
  reference: number[]
  /** The tonic the exercise was built on, spelled without double accidentals. */
  rootNote: NoteName
  quality: PitchExerciseQuality
  /** Seconds each reference note sounds, and the length of the sung note it expects back. */
  holdSeconds: number
  /** The `vocal-ex-*` lexicon entry describing this exercise type. */
  lexiconId: string
  /** A factual label for the item — data, not pedagogical copy. */
  label: string
  /**
   * Each reference note's name in the exercise's own key, index-aligned with `reference`: a
   * letter and at most one accidental, with no octave. Shown with `spelledNoteLabel`, because
   * the octave is only known after the range transposition.
   */
  spelling: string[]
  /** The interval an interval exercise asks for; null for every other type. */
  interval: PitchIntervalKind | null
}

export interface PitchSessionResult {
  question: PitchQuestion
  /** `absolute.mean` for the item, or null when nothing was heard. */
  meanScore: number | null
}

export interface PitchSessionStats {
  /** Items that produced a score. An item nobody sang is not a zero. */
  scored: number
  total: number
  /** Mean of the scored items, 0-100. */
  meanPercent: number
}

const pick = <T>(pool: readonly T[], rng: () => number): T => pool[Math.floor(rng() * pool.length)]

const LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'] as const
const LETTER_PITCH = [0, 2, 4, 5, 7, 9, 11] as const

const accidentalOf = (name: string) => (name.endsWith('#') ? 1 : name.endsWith('b') ? -1 : 0)

/**
 * The note `letterSteps` letters and `semitones` semitones above `root`, spelled from the letter
 * first and the accidental second, the way a musician names it: the third degree of Eb major is
 * G, two letters above E, not whatever the pitch class happens to be called with sharps.
 */
const spellAbove = (root: NoteName, letterSteps: number, semitones: number): string => {
  const rootLetter = LETTERS.indexOf(root[0] as (typeof LETTERS)[number])
  const letter = (rootLetter + letterSteps) % 7
  const rootPitch = LETTER_PITCH[rootLetter] + accidentalOf(root)
  const natural = LETTER_PITCH[letter] + 12 * Math.floor((rootLetter + letterSteps) / 7)
  const accidental = rootPitch + semitones - natural
  const sign = accidental > 0 ? '#' : 'b'
  return `${LETTERS[letter]}${sign.repeat(Math.abs(accidental))}`
}

/**
 * A spelled name and the MIDI number it sounds at, as a label: "Eb4". The octave belongs to the
 * letter, so B#4 sounds as C5 and Cb4 as B3 — which is how a score writes them.
 */
export const spelledNoteLabel = (name: string, midi: number): string => {
  const letter = LETTERS.indexOf(name[0] as (typeof LETTERS)[number])
  const alter = name.endsWith('#') ? name.length - 1 : -(name.length - 1)
  const octave = Math.round((midi - alter - LETTER_PITCH[letter]) / 12) - 1
  return `${name}${octave}`
}

/**
 * Major for the first half of a session, natural minor for the second (spec §6, Bowie GH #2328).
 * A singer warms up in the mode they already hear, and meets the lowered third and sixth after.
 */
export const qualityForItem = (index: number, total: number): PitchExerciseQuality =>
  index < Math.ceil(total / 2) ? 'major' : 'minor'

const tonicMidi = (rootNote: NoteName) => NOMINAL_TONIC_MIDI + NoteLib.getNoteChromaticIndex(rootNote)

const stepsFor = (quality: PitchExerciseQuality) => (quality === 'major' ? MAJOR_STEPS : MINOR_STEPS)

/** Semitones above the tonic for a 1-indexed scale degree. */
const degreeSemitones = (degree: number, quality: PitchExerciseQuality) => stepsFor(quality)[degree - 1]

/**
 * Name a MIDI number for display — the calibrated range, and the pitch the audition plays.
 *
 * Sharp-spelled, because a measured range has no key to be spelled in: the singer's lowest
 * comfortable note is a frequency, not a scale degree. Rounds first, since every measurement
 * this tool takes is fractional by design.
 */
export const midiToNoteLabel = (midi: number): string => {
  const rounded = Math.round(midi)
  return `${NoteLib.getNoteByIndex(((rounded % 12) + 12) % 12)}${Math.floor(rounded / 12) - 1}`
}

/**
 * Exercise type 1 — one note, sustained. The atomic skill, and the smoke test for whether the
 * tracker is working for this user on this hardware at all.
 *
 * RNG draw order: 1. root index into `MAJOR_TONICS`.
 */
export const generateToneQuestion = (rng: () => number = Math.random): PitchQuestion => {
  const rootNote = pick(MAJOR_TONICS, rng)
  return {
    type: 'tone',
    reference: [tonicMidi(rootNote)],
    rootNote,
    quality: 'major',
    holdSeconds: TONE_HOLD_SECONDS,
    lexiconId: 'vocal-ex-match-tone',
    label: rootNote,
    spelling: [rootNote],
    interval: null,
  }
}

/**
 * Exercise type 2 — the root, then the interval above it. Ascending only in v1 (§6).
 *
 * RNG draw order: 1. root index into `MAJOR_TONICS`; 2. interval index into the level's set.
 */
export const generateSungIntervalQuestion = (
  level: PitchIntervalLevel,
  rng: () => number = Math.random
): PitchQuestion => {
  const rootNote = pick(MAJOR_TONICS, rng)
  const kind = pick(INTERVAL_LEVEL_SETS[level], rng)
  const root = tonicMidi(rootNote)
  return {
    type: 'interval',
    reference: [root, root + INTERVAL_SEMITONES[kind]],
    rootNote,
    quality: 'major',
    holdSeconds: NOTE_SECONDS,
    lexiconId: 'vocal-ex-interval',
    label: `${kind} above ${rootNote}`,
    spelling: [rootNote, spellAbove(rootNote, INTERVAL_LETTER_STEPS[kind], INTERVAL_SEMITONES[kind])],
    interval: kind,
  }
}

/** Each scale degree in the key, spelled one letter per degree */
const spellDegrees = (rootNote: NoteName, degrees: readonly number[], quality: PitchExerciseQuality) =>
  degrees.map(degree => spellAbove(rootNote, degree - 1, degreeSemitones(degree, quality)))

/**
 * Exercise type 3 — a canonical warm-up pattern, major or natural minor.
 *
 * RNG draw order: 1. root index; 2. pattern index; 3. quality (< 0.5 major). The quality draw is
 * taken even when `forcedQuality` decides it, so a seeded session draws the same roots either way.
 */
export const generateVocaliseQuestion = (
  rng: () => number = Math.random,
  forcedQuality?: PitchExerciseQuality
): PitchQuestion => {
  const rootDraw = rng()
  const pattern = pick(VOCALISE_PATTERNS, rng)
  const drawnQuality: PitchExerciseQuality = rng() < 0.5 ? 'major' : 'minor'
  const quality = forcedQuality ?? drawnQuality
  const tonics = quality === 'major' ? MAJOR_TONICS : MINOR_TONICS
  const rootNote = tonics[Math.floor(rootDraw * tonics.length)]
  const root = tonicMidi(rootNote)
  return {
    type: 'vocalise',
    reference: pattern.degrees.map(degree => root + degreeSemitones(degree, quality)),
    rootNote,
    quality,
    holdSeconds: NOTE_SECONDS,
    lexiconId: 'vocal-ex-vocalise',
    label: `${rootNote} ${quality} · ${pattern.degrees.join('-')}`,
    spelling: spellDegrees(rootNote, pattern.degrees, quality),
    interval: null,
  }
}

/**
 * Exercise type 4 — a short diatonic phrase, 3-5 notes, stepwise-dominant, closing on scale
 * degree 1 or 3 so the phrase ends somewhere the ear accepts (§6).
 *
 * Built backwards from the close, because the close is the constrained end. At most
 * `floor(moves * 0.3)` of the moves are thirds, which makes the >= 70 % step ratio true by
 * construction rather than by rejection sampling — a generator that retried until it passed
 * would be a generator whose distribution nobody could describe.
 *
 * RNG draw order: 1. quality; 2. root index; 3. length; 4. closing degree; then, per move,
 * a leap-or-step draw and a direction draw.
 */
export const generatePhraseQuestion = (
  rng: () => number = Math.random,
  forcedQuality?: PitchExerciseQuality
): PitchQuestion => {
  const drawnQuality: PitchExerciseQuality = rng() < 0.5 ? 'major' : 'minor'
  const quality = forcedQuality ?? drawnQuality
  const tonics = quality === 'major' ? MAJOR_TONICS : MINOR_TONICS
  const rootNote = pick(tonics, rng)
  const length = 3 + Math.floor(rng() * 3)
  const closing = rng() < 0.5 ? 1 : 3

  const moves = length - 1
  let leapsLeft = Math.floor(moves * 0.3)
  const degrees = [closing]

  for (let move = 0; move < moves; move++) {
    const wantsLeap = leapsLeft > 0 && rng() >= 0.75
    const size = wantsLeap ? 2 : 1
    const previous = degrees[0]
    const candidates = [previous + size, previous - size].filter(degree => degree >= 1 && degree <= 8)
    /** `candidates` is never empty: degrees 1..8 always admit a move of 1 or 2 in one direction. */
    const chosen = candidates[Math.floor(rng() * candidates.length)]
    if (wantsLeap) {
      leapsLeft--
    }
    degrees.unshift(chosen)
  }

  const root = tonicMidi(rootNote)
  return {
    type: 'phrase',
    reference: degrees.map(degree => root + degreeSemitones(degree, quality)),
    rootNote,
    quality,
    holdSeconds: NOTE_SECONDS,
    lexiconId: 'vocal-ex-phrase',
    label: `${rootNote} ${quality}`,
    spelling: spellDegrees(rootNote, degrees, quality),
    interval: null,
  }
}

/**
 * Dispatch on the exercise type. `level` is consumed by the interval generator only, and
 * `quality` (from `qualityForItem`) by the two generators that have a mode.
 */
export const generatePitchQuestion = (
  type: PitchExerciseType,
  level: PitchIntervalLevel,
  rng: () => number = Math.random,
  quality?: PitchExerciseQuality
): PitchQuestion => {
  if (type === 'tone') {
    return generateToneQuestion(rng)
  }
  if (type === 'interval') {
    return generateSungIntervalQuestion(level, rng)
  }
  return type === 'vocalise' ? generateVocaliseQuestion(rng, quality) : generatePhraseQuestion(rng, quality)
}

/**
 * The shortest sustained note §5.8's calibration will accept, in frames.
 *
 * One second at the 21 ms analysis hop. The spec asks the singer to *sustain* their extreme, and
 * a scoop, a cough or a door closing can all clear the voicing gate for a tenth of a second. A
 * range built from one of those would push every exercise into a tessitura the singer never
 * agreed to, and nothing downstream could tell.
 */
const CALIBRATION_MIN_FRAMES = 48

/**
 * The pitch of a calibration take: the median of everything that passed the voicing gate, or
 * null when there was not enough of it. Median, not mean, so one octave-error frame does not
 * move the singer's recorded extreme by a fifth.
 *
 * Null is a real answer — "we did not hear enough to call this your lowest note" — and the
 * caller must not store a guess in its place.
 */
export const calibrationMidi = (frames: PitchFrame[]): number | null => {
  const voiced = voicedFrames(frames)
  if (voiced.length < CALIBRATION_MIN_FRAMES) {
    return null
  }
  return median(voiced.map(frame => midiNoteNumberFromFrequency(frame.frequency)))
}

/**
 * Fold a finished session into its headline number: the mean of the items that were scored.
 *
 * An item nobody sang is excluded rather than counted as zero — the tool measures pitch, and a
 * microphone that was muted is not a pitch. `scored` is reported alongside `total` so the
 * summary can say which of the two numbers the percentage is of.
 */
export const computePitchSessionStats = (results: PitchSessionResult[]): PitchSessionStats => {
  const scores = results.map(result => result.meanScore).filter((score): score is number => score !== null)
  const meanPercent =
    scores.length === 0 ? 0 : Math.round((scores.reduce((total, score) => total + score, 0) / scores.length) * 100)
  return { scored: scores.length, total: results.length, meanPercent }
}
